import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { CompactSelect } from 'components/Shared';
import { FindMany, SelectOption } from 'contracts/Common';
import { useDocks, useCompanies } from 'hooks';
import { throttle } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as S from './styles';

export interface FindIntervals extends FindMany {
  companyId?: unknown;
  dockId?: unknown;
}

interface Props {
  onFilter?: (query: FindIntervals) => void;
  currentFilter: FindIntervals;
  delay?: number;
}

interface GeneralState {
  companyId?: string | number;
}

export const IntervalFilters: React.FC<Props> = ({
  delay = 1000,
  currentFilter,
  onFilter,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { companyOptions, loadingCompanies, fetchCompanies } =
    useCompanies();
  const { dockOptions, loadingDocks, fetchDocks } = useDocks();

  const [generalState, setGeneralState] = useState<GeneralState>({});
  const [filters, setFilters] = useState<FindIntervals>({
    ...currentFilter,
    dirty: false,
  });

  const handleWarehouseChange = useCallback(
    (option: SelectOption | null): void => {
      formRef.current?.setFieldValue('dockId', null);
      setFilters((state) => ({
        ...state,
        // if dockId is present on currentFilter, dont make the filter dirty right now
        dirty: currentFilter?.dockId ? false : true,
        companyId: option?.value,
      }));
      setGeneralState((state) => ({
        ...state,
        companyId: option?.value,
      }));
      if (option) {
        fetchDocks({ companyId: option.value });
      }
    },
    [currentFilter?.dockId, fetchDocks]
  );

  const handleDockChange = useCallback((option: SelectOption | null): void => {
    setFilters((state) => ({
      ...state,
      dirty: true,
      dockId: option?.value,
    }));
  }, []);

  const setDefaultDock = useCallback(() => {
    if (!currentFilter?.dockId) return;

    const option = dockOptions.find(
      (option) => option.value === currentFilter.dockId
    );

    if (option) {
      formRef.current?.setFieldValue('dockId', option);
    }
  }, [currentFilter, dockOptions]);

  // throttled methods

  const onWarehouseChange = useMemo(
    () => throttle(handleWarehouseChange, delay),
    [delay, handleWarehouseChange]
  );

  const onDockChange = useMemo(
    () => throttle(handleDockChange, delay),
    [delay, handleDockChange]
  );

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    setDefaultDock();
  }, [setDefaultDock]);

  const invokeOnFilter = useCallback((): void => {
    if (filters.dirty && onFilter) {
      onFilter(filters);
    }
  }, [filters, onFilter]);

  useEffect(() => {
    invokeOnFilter();
  }, [invokeOnFilter]);

  return (
    <S.Container>
      <Form ref={formRef} onSubmit={() => {}}>
        <CompactSelect
          name="companyId"
          placeholder="Armazém"
          options={companyOptions}
          isLoading={loadingCompanies}
          onChange={onWarehouseChange}
        />
        <CompactSelect
          name="dockId"
          placeholder="Doca"
          options={dockOptions}
          isLoading={loadingDocks}
          onChange={onDockChange}
          isDisabled={!generalState.companyId}
        />
      </Form>
    </S.Container>
  );
};
